import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DelayEventModal } from "./FlightContextMenuModals/DelayEventModal";
import { GroundTurnBackModal } from "./FlightContextMenuModals/GroundTurnBackModal";
import { SetNewStartTimeModal } from "./FlightContextMenuModals/SetNewStartTimeModal";
import { DeleteEventModal } from "./FlightContextMenuModals/DeleteEventModal";
import { CancelEventModal } from "./FlightContextMenuModals/CancelEventModal";
import { ConfigAircraftModal } from "./FlightContextMenuModals/ConfigAircraftModal";
import { SwapAircraftModal } from "./FlightContextMenuModals/SwapAircraftModal";
import { AssignFlightsToPairingModal } from "./FlightContextMenuModals/AssignFlightsToPairingModal";
import { BulkEditEventModal } from "./FlightContextMenuModals/BulkEditEventModal";
import { ReleaseFlightModal } from "./FlightContextMenuModals/ReleaseFlightModal";
import { CustomActionModal } from "./FlightContextMenuModals/CustomActionModal";
import { SplitPairingModal } from "./PairingContextMenuModals/SplitPairingModal";
import { DeletePairingModal } from "./PairingContextMenuModals/DeletePairingModal";
import { AssignCrewToPairingModal } from "./PairingContextMenuModals/AssignCrewToPairingModal";
import { SendAPISMessageModal } from "./FlightContextMenuModals/SendAPISMessageModal";
import { CreatePairingFromFlightsModal } from "./FlightContextMenuModals/CreatePairingModal";
import { UnassignFlightModal } from "./FlightContextMenuModals/UnassignFlightModal";
import { ReassignFlightModal } from "./FlightContextMenuModals/ReassignFlightModal";
import { BidAssignmentDirty } from "./BidAssignmentModals/BidAssignmentDirty";
import { SwapFlightModal } from "./FlightContextMenuModals/SwapFlightModal";
import { alpha, Box, Theme } from "@mui/material";
import { PairingErrorModal } from "./PairingContextMenuModals/PairingErrorModal";
import { setInteractiveState } from "src/redux/actions/flights/FlightTimeline.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { InteractiveState } from "src/redux/reducers/FlightTimelineReducer";
import { performUnassign } from "./FlightContextMenuModals/UnassignFlightModal";
import { IFlight, IGroundEvent } from "@vmosolutions/oc-interfaces";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";

export enum EventModalTypes {
  None,
  GroundTurnBack,
  DelayFlights,
  DelayEvent,
  SetNewStartTime,
  CancelEvent,
  DeleteEvent,
  ConfigAircraft,
  SwapAircraft,
  AssignToPairing,
  BulkEditEvents,
  ReleaseFlight,
  CustomActions,
  SplitPairing,
  DeletePairing,
  AssignCrewToPairing,
  SendAPISMessage,
  CreatePairing,
  BidAssignmentDirty,
  UnassignFlight,
  ReassignFlight,
  ReassignGroundEvent,
  SwapFlights,
  SwapGroundEvents,
  PairingError,
}

interface IProps {
  modalType: EventModalTypes;
  setActiveEventModal: (ModalTypes: EventModalTypes) => void;
  id?: string;
  modalProps?: any; // used to pass in properties required by some of the modal windows. (rather than use redux.)
}

export const DialogModalStyles = (theme: Theme): object => ({
  display: "flex",
  justifyContent: "center",
  overflow: "hidden",
  alignItems: "center",
  position: "absolute",
  zIndex: 1300,
  width: "100%",
  height: "100%",
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  "& .modal_content": {
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    justifyContent: "center",
    borderRadius: 3,
    padding: "20px 10px 10px 10px",
    margin: "48px",
    maxHeight: "calc(100% - 96px)",
    maxWidth: 600,
    position: "relative",
    boxShadow:
      "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
    "& .custom_action_form": {
      margin: "0px 20px",
      "& .form_footer": {
        marginTop: "10px",
        display: "flex",
        justifyContent: "flex-start",
        "& button": { margin: "5px 5px" },
        "& .btn": {
          flexWrap: "wrap",
          display: "flex",
          height: 27,
          fontSize: 10,
          lineHeight: "12px",
          padding: "2px 8px",
          margin: "0px 4px",
          "&.submit": {
            color: theme.custom.forms.inputBack,
          },
        },
      },
    },
    "& .slider-container": {
      padding: "0px 10px 0px 10px",
    },
    "& .modal_selection": {},
    "& .modal_status": {
      fontSize: 12,
      fontWeight: 300,
      color: theme.palette.primary.contrastText,
      paddingLeft: "25px",
    },
    "& .bulk_fields": {
      marginRight: "10px",
      "& .bulk_field_selector": { width: 510 },
      "& .bulk_selector": { width: 250 },
    },
    "& .unreleased_icon": {
      width: 14,
      height: 14,
      borderRadius: "50%",
      backgroundColor: "#8D65C5",
      marginRight: "5px",
      marginTop: "-2px",
    },
  },

  "& .title": {
    fontSize: 25,
    color: theme.custom.modal.title,
  },

  "& .message": {
    padding: "0px 20px",
    fontSize: 16,
    color: theme.custom.modal.message,
  },

  "& .datetimeinput": {
    flexDirection: "row",
  },

  "& .error": {
    color: theme.custom.modal.error,
    padding: "0px 20px",
  },

  "& .warning": {
    color: theme.custom.modal.warning,
    padding: "0px 20px",
  },

  "& .modalContent": {
    width: "500px",
  },

  "& .datetimeInput": {
    "& .input_control": {},
    alignItems: "end",
  },

  "& .modalTextfield": {
    "& .input_control": {
      "& .input-base": { width: "263px" },
    },
  },

  "& .slider": {
    paddingBottom: "30px",
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },
});

export const DialogModalWrapper = (props: IProps): JSX.Element => {
  const [open, setOpen] = useState<boolean>(true);
  const tailSwap = useSelector((state: IRootState) => state.authentication.appSettings?.warnTailSwap);
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );

  const selectedGroundEvents: IGroundEvent[] = useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => !isFlight(event)) as IGroundEvent[];
  }, [selectedEvents]);

  const selectedFlights: IFlight[] = useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const dispatch = useDispatch();

  const byPassWarning = useCallback(
    (state?: InteractiveState): any => {
      setOpen(false);
      props.setActiveEventModal(EventModalTypes.None);
      if (state) dispatch(setInteractiveState(state, props.id));
      return null;
    },
    [props, dispatch]
  );

  const ModalToShow = (): JSX.Element => {
    switch (props.modalType) {
      case EventModalTypes.ReleaseFlight:
        return (
          <ReleaseFlightModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
        );
      case EventModalTypes.CustomActions:
        return (
          <CustomActionModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            modalProps={props?.modalProps}
          />
        );
      case EventModalTypes.GroundTurnBack:
        return (
          <GroundTurnBackModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
        );
      case EventModalTypes.None:
        return null;
      case EventModalTypes.DelayEvent:
        return <DelayEventModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />;
      case EventModalTypes.DeleteEvent:
        return <DeleteEventModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />;
      case EventModalTypes.SetNewStartTime:
        return (
          <SetNewStartTimeModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
        );
      case EventModalTypes.CancelEvent:
        return <CancelEventModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />;
      case EventModalTypes.ConfigAircraft:
        return (
          <ConfigAircraftModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
        );
      case EventModalTypes.SwapAircraft:
        return (
          <SwapAircraftModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            windowId={props.id}
          />
        );
      case EventModalTypes.SplitPairing:
        return (
          <SplitPairingModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
        );
      case EventModalTypes.DeletePairing:
        return (
          <DeletePairingModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
        );
      case EventModalTypes.AssignCrewToPairing:
        return (
          <AssignCrewToPairingModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            modalProps={props?.modalProps}
          />
        );
      default:
      case EventModalTypes.SendAPISMessage:
        return (
          <SendAPISMessageModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            id={props.id}
          />
        );
      case EventModalTypes.AssignToPairing:
        return (
          <AssignFlightsToPairingModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
          />
        );
      case EventModalTypes.CreatePairing:
        return (
          <CreatePairingFromFlightsModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
          />
        );
      case EventModalTypes.BulkEditEvents:
        return (
          <BulkEditEventModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
        );

      case EventModalTypes.BidAssignmentDirty:
        return (
          <BidAssignmentDirty
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            modalProps={props?.modalProps}
          />
        );
      case EventModalTypes.UnassignFlight:
        if (tailSwap) {
          return (
            <UnassignFlightModal isOpen={open} handleClose={setOpen} setActiveEventModal={props.setActiveEventModal} />
          );
        } else {
          performUnassign(selectedFlights, selectedGroundEvents, dispatch);
          return byPassWarning(InteractiveState.None);
        }
      case EventModalTypes.ReassignFlight:
        if (tailSwap) {
          return (
            <ReassignFlightModal
              isOpen={open}
              handleClose={setOpen}
              setActiveEventModal={props.setActiveEventModal}
              windowId={props.id}
              isGroundEvent={false}
            />
          );
        } else {
          return byPassWarning(InteractiveState.Reassign);
        }
      case EventModalTypes.ReassignGroundEvent:
        if (tailSwap) {
          return (
            <ReassignFlightModal
              isOpen={open}
              handleClose={setOpen}
              setActiveEventModal={props.setActiveEventModal}
              windowId={props.id}
              isGroundEvent={true}
            />
          );
        } else {
          return byPassWarning(InteractiveState.Reassign);
        }
      case EventModalTypes.SwapFlights:
        return (
          <SwapFlightModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            modalProps={props?.modalProps}
            isFlight={true}
          />
        );
      case EventModalTypes.SwapGroundEvents:
        return (
          <SwapFlightModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            modalProps={props?.modalProps}
            isFlight={false}
          />
        );
      case EventModalTypes.PairingError:
        return (
          <PairingErrorModal
            isOpen={open}
            handleClose={setOpen}
            setActiveEventModal={props.setActiveEventModal}
            modalProps={props?.modalProps}
          />
        );
    }
  };

  // Memoize the modal to prevent re-renders when something changes in the parent component
  const MemoizedModal = useMemo(() => {
    return ModalToShow();
  }, [props.modalType, props.modalProps, props.id]);

  return MemoizedModal;
};
